// =============================================================================
// BP-07-03: useResourceSearch Hook
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { searchResources, searchDiagnoses } from '@/features/resources/api/resourcesApi';
import type {
  ResourceSearchParams,
  Resource,
  Diagnosis,
  PaginatedResponse,
} from '@/features/resources/types/resourcesTypes';
import { resourceQueryKeys } from '@/features/resources/types/resourcesTypes';

const DEBOUNCE_MS = 300;

/**
 * Hook for debounced resource search
 */
export function useResourceSearch(params: ResourceSearchParams, enabled = true) {
  const [debouncedQuery, setDebouncedQuery] = useState(params.q ?? '');

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(params.q);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [params.q]);

  const searchParams = { ...params, q: debouncedQuery };

  return useQuery({
    queryKey: resourceQueryKeys.search(searchParams),
    queryFn: () => searchResources(searchParams),
    enabled: enabled && debouncedQuery.length >= 2,
    staleTime: 2 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook for infinite scroll search results
 */
export function useInfiniteResourceSearch(
  params: Omit<ResourceSearchParams, 'page'>,
  enabled = true
) {
  const [debouncedQuery, setDebouncedQuery] = useState(params.q);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(params.q);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [params.q]);

  return useInfiniteQuery({
    queryKey: [...resourceQueryKeys.all, 'search', 'infinite', { ...params, q: debouncedQuery }],
    queryFn: ({ pageParam = 1 }) =>
      searchResources({ ...params, q: debouncedQuery, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<Resource>) => {
      return lastPage.hasNext ? lastPage.page + 1 : undefined;
    },
    enabled: enabled && debouncedQuery.length >= 2,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for diagnosis search (typeahead)
 */
export function useDiagnosisSearch(query: string) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  return useQuery({
    queryKey: ['diagnosis-search', debouncedQuery],
    queryFn: () => searchDiagnoses(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Custom hook for managing search state
 */
export function useSearchState(initialQuery = '') {
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<Partial<ResourceSearchParams>>({});

  const updateQuery = useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  const updateFilters = useCallback((newFilters: Partial<ResourceSearchParams>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const clearAll = useCallback(() => {
    setQuery('');
    setFilters({});
  }, []);

  const searchParams: ResourceSearchParams = {
    q: query,
    ...filters,
  };

  return {
    query,
    filters,
    searchParams,
    updateQuery,
    updateFilters,
    clearFilters,
    clearAll,
    hasFilters: Object.keys(filters).length > 0,
    hasQuery: query.length > 0,
  };
}

/**
 * Hook for search suggestions (typeahead)
 */
export function useSearchSuggestions(query: string) {
  const resourceSearch = useResourceSearch({ q: query, limit: 5 }, query.length >= 2);
  const diagnosisSearch = useDiagnosisSearch(query);

  return {
    resources: resourceSearch.data?.items ?? [],
    diagnoses: diagnosisSearch.data ?? [],
    isLoading: resourceSearch.isLoading || diagnosisSearch.isLoading,
    hasResults:
      (resourceSearch.data?.items?.length ?? 0) > 0 ||
      (diagnosisSearch.data?.length ?? 0) > 0,
  };
}

/**
 * Highlight search terms in text
 */
export function highlightSearchTerms(text: string, query: string): string {
  if (!query.trim()) return text;

  const terms = query.split(/\s+/).filter((term) => term.length >= 2);
  if (terms.length === 0) return text;

  const regex = new RegExp(`(${terms.join('|')})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
}

export default useResourceSearch;

